/**
 * Branch opening hours: `{mon..sun: [["HH:MM","HH:MM"], ...]}` in Europe/Athens wall-clock time.
 * - close <= open means the interval runs past midnight (["08:00","02:00"] = 08:00 to 02:00 next day)
 * - [] means closed that day
 * - a missing day (or missing hours) means unknown, and no open/closed badge is shown
 */
export type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export type Interval = [string, string];
export type Hours = Partial<Record<DayKey, Interval[]>>;
export type Lang = 'gr' | 'en';

export const DAY_KEYS: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// ponytail: fixed Athens tz (correct for Greece and Cyprus); per-branch tz when a non-Greek chain signs up.
export const BRANCH_TIME_ZONE = 'Europe/Athens';

const DAY_NAMES: Record<Lang, string[]> = {
  gr: ['Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο', 'Κυριακή'],
  en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
};

const athensClock = new Intl.DateTimeFormat('en-GB', {
  timeZone: BRANCH_TIME_ZONE,
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

export function athensNow(now: Date): { day: DayKey; minutes: number } {
  const parts = athensClock.formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const day = get('weekday').slice(0, 3).toLowerCase() as DayKey;
  const hour = Number(get('hour')) % 24;
  return { day, minutes: hour * 60 + Number(get('minute')) };
}

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;

function toMinutes(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const m = HHMM.exec(value);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** Valid intervals of a day as minutes; null when the day is unknown. */
function dayIntervals(hours: Hours, day: DayKey): Array<{ open: number; close: number; o: string; c: string }> | null {
  const raw = hours[day];
  if (!Array.isArray(raw)) return null;
  const out: Array<{ open: number; close: number; o: string; c: string }> = [];
  for (const iv of raw) {
    if (!Array.isArray(iv)) continue;
    const open = toMinutes(iv[0]);
    const close = toMinutes(iv[1]);
    if (open === null || close === null) continue;
    out.push({ open, close, o: iv[0], c: iv[1] });
  }
  return out;
}

export interface OpenState {
  open: boolean;
  /** Closing time of the current interval (when open). */
  until?: string;
  /** Next opening time (when closed and the branch opens again within a week). */
  opensAt?: string;
  /** 0 = later today, 1 = tomorrow, n = in n days. */
  opensInDays?: number;
}

export function openState(hours: Hours | undefined | null, now: Date): OpenState | null {
  if (!hours || typeof hours !== 'object') return null;
  const { day, minutes } = athensNow(now);
  const idx = DAY_KEYS.indexOf(day);
  if (idx === -1) return null;

  // Yesterday's past-midnight intervals spill into today.
  const yesterday = dayIntervals(hours, DAY_KEYS[(idx + 6) % 7]) || [];
  for (const iv of yesterday) {
    if (iv.close <= iv.open && minutes < iv.close) return { open: true, until: iv.c };
  }

  const today = dayIntervals(hours, day);
  if (today === null) return null;
  for (const iv of today) {
    const pastMidnight = iv.close <= iv.open;
    if (pastMidnight ? minutes >= iv.open : minutes >= iv.open && minutes < iv.close) {
      return { open: true, until: iv.c };
    }
  }

  // Closed: find the next opening today, then on the following days.
  const laterToday = today.filter((iv) => iv.open > minutes).sort((a, b) => a.open - b.open)[0];
  if (laterToday) return { open: false, opensAt: laterToday.o, opensInDays: 0 };
  for (let n = 1; n <= 7; n++) {
    const next = (dayIntervals(hours, DAY_KEYS[(idx + n) % 7]) || []).sort((a, b) => a.open - b.open)[0];
    if (next) return { open: false, opensAt: next.o, opensInDays: n };
  }
  return { open: false };
}

export function formatOpenLabel(state: OpenState, lang: Lang, now?: Date): string {
  if (state.open) {
    return lang === 'en' ? `Open · closes ${state.until}` : `Ανοιχτό · κλείνει ${state.until}`;
  }
  if (!state.opensAt) return lang === 'en' ? 'Closed' : 'Κλειστό';
  let when = '';
  if (state.opensInDays === 1) {
    when = lang === 'en' ? 'tomorrow ' : 'αύριο ';
  } else if (state.opensInDays && state.opensInDays > 1 && now) {
    const idx = DAY_KEYS.indexOf(athensNow(now).day);
    when = `${DAY_NAMES[lang][(idx + state.opensInDays) % 7]} `;
  }
  return lang === 'en' ? `Closed · opens ${when}${state.opensAt}` : `Κλειστό · ανοίγει ${when}${state.opensAt}`;
}

export interface WeekRow {
  day: DayKey;
  label: string;
  text: string;
  isToday: boolean;
}

/** Rows for the weekly hours table, Monday first, today flagged. */
export function weekRows(hours: Hours | undefined | null, lang: Lang, now: Date): WeekRow[] {
  if (!hours || typeof hours !== 'object') return [];
  const today = athensNow(now).day;
  return DAY_KEYS.map((day, i) => {
    const ivs = dayIntervals(hours, day);
    let text: string;
    if (ivs === null) text = '—';
    else if (ivs.length === 0) text = lang === 'en' ? 'Closed' : 'Κλειστά';
    else text = ivs.map((iv) => `${iv.o}–${iv.c}`).join(', ');
    return { day, label: DAY_NAMES[lang][i], text, isToday: day === today };
  });
}
